import TeamService from '@/services/TeamService'

const state = () => ({
  items: [],
  isLoading: false,
  error: null,
  lastLoadedAt: 0,

  // NEW: effective access map for current auth user
  // shape: { [targetUserId]: { listen:boolean, whisper:boolean, barge:boolean } }
  effectiveAccessByUserId: {},
  effectiveIsLoading: false,
  effectiveError: null,
  effectiveLastLoadedAt: 0
})

const mutations = {
  SET_LOADING(state, v) {
    state.isLoading = !!v
  },
  SET_ERROR(state, v) {
    state.error = v || null
  },
  SET_ITEMS(state, rows) {
    state.items = Array.isArray(rows) ? rows : []
    state.lastLoadedAt = Date.now()
  },
  REMOVE_ITEM(state, id) {
    const key = String(id)
    state.items = (state.items || []).filter(u => String(u.id) !== key)

    // keep effective map clean if a user disappears
    if (state.effectiveAccessByUserId && state.effectiveAccessByUserId[key]) {
      const next = { ...state.effectiveAccessByUserId }
      delete next[key]
      state.effectiveAccessByUserId = next
    }
  },

  // ---- NEW (effective access) ----
  SET_EFFECTIVE_LOADING(state, v) {
    state.effectiveIsLoading = !!v
  },
  SET_EFFECTIVE_ERROR(state, v) {
    state.effectiveError = v || null
  },
  SET_EFFECTIVE_ACCESS(state, map) {
    state.effectiveAccessByUserId = map && typeof map === 'object' ? map : {}
    state.effectiveLastLoadedAt = Date.now()
  }
}

// Parses API response:
// { data: { "56": { listen:true, whisper:true, barge:true }, ... } }
function parseEffectiveMap(payload) {
  const root = payload?.data ?? payload
  if (!root || typeof root !== 'object' || Array.isArray(root)) return {}

  const out = {}
  for (const [userId, perms] of Object.entries(root)) {
    out[String(userId)] = {
      listen: !!perms?.listen,
      whisper: !!perms?.whisper,
      barge: !!perms?.barge
    }
  }
  return out
}

const actions = {
  async fetch({ commit }, params = {}) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const resp = await TeamService.list(params)

      const payload = resp?.data
      const rows =
        Array.isArray(payload) ? payload :
        Array.isArray(payload?.data) ? payload.data :
        Array.isArray(payload?.users) ? payload.users :
        []

      commit('SET_ITEMS', rows)
      return rows
    } catch (e) {
      commit('SET_ERROR', e?.response?.data?.message || e?.message || 'Failed to load team')
      throw e
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // NEW: load effective access map for current auth user
  async fetchEffective({ commit }, params = {}) {
    commit('SET_EFFECTIVE_LOADING', true)
    commit('SET_EFFECTIVE_ERROR', null)
    try {
      const resp = await TeamService.effective(params)
      const map = parseEffectiveMap(resp?.data)
      commit('SET_EFFECTIVE_ACCESS', map)
      return map
    } catch (e) {
      commit('SET_EFFECTIVE_ERROR', e?.response?.data?.message || e?.message || 'Failed to load effective access')
      // do NOT throw; team view should still render
      commit('SET_EFFECTIVE_ACCESS', {})
      return {}
    } finally {
      commit('SET_EFFECTIVE_LOADING', false)
    }
  },

  async destroy({ commit }, id) {
    await TeamService.destroy(id)
    commit('REMOVE_ITEM', id)
  }
}

const getters = {
  items: (s) => s.items || [],
  byId: (s) => (id) => (s.items || []).find(u => String(u.id) === String(id)) || null,

  // NEW: effective access getters
  effectiveMap: (s) => s.effectiveAccessByUserId || {},
  effectiveByUserId: (s) => (id) => {
    const key = String(id)
    return (s.effectiveAccessByUserId || {})[key] || { listen: false, whisper: false, barge: false }
  },
  effectiveIsLoading: (s) => !!s.effectiveIsLoading,
  effectiveError: (s) => s.effectiveError
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
